import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Plus, Edit, Building2, MapPin, Paperclip, X, Link, FileText, Eye, Download, Power, PowerOff, Trash2, AlertTriangle, Package, Info } from 'lucide-react';
import { toast } from 'sonner';
import { ModulePageHeader } from '../components/ModulePageHeader';
import { validateFileSize } from '../lib/uploadUtils';
import { downloadSecureFile, openSecureFile } from '../lib/secureFileAccess';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useAutoSave, AutoSaveStatus } from '../hooks/useAutoSave';
import FacilityProductionSection from '../components/FacilityProductionSection';
import {
  createDraftEvidence,
  deleteEvidenceFiles,
  draftEvidence,
  getEvidenceFileId,
  persistedEvidence,
  revokeDraftEvidence,
  revokeDraftEvidences,
  toEvidencePayload,
  uploadDraftEvidences,
} from '../lib/draftEvidence';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to ensure URL has proper protocol for external links
const ensureProtocol = (url) => {
  if (!url) return url;
  // If it's an internal API URL or already has a protocol, return as-is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/api/')) {
    return url;
  }
  // Add https:// to external URLs
  return `https://${url}`;
};

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Germany', 'France', 'Australia', 
  'Canada', 'Japan', 'China', 'Brazil', 'Other'
];

const createProductionDraft = (yearType = 'financial_year') => {
  const year = new Date().getFullYear();
  return { reportingYear: yearType === 'calendar_year' ? String(year) : `FY ${year}-${String(year + 1).slice(-2)}`, inputType: 'yearly', unit: 'MT', quantity: '', monthlyData: {} };
};
const PRODUCTION_MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

export default function Facilities() {
  const [facilities, setFacilities] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [pincodeError, setPincodeError] = useState('');
  const [showInactive, setShowInactive] = useState(false); // Show inactive facilities toggle
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [facilityToToggle, setFacilityToToggle] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState(null);
  const [sameAsOrg, setSameAsOrg] = useState(false);
  const [autoSavedId, setAutoSavedId] = useState(null); // Track ID from auto-save create
  const [productionDraft, setProductionDraft] = useState(createProductionDraft());
  const pendingDeletionIds = useRef(new Set());
  const { getAuthHeader, user, subscriptionExpired } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    products_services: '',  // Renamed from products_manufactured
    machinery_equipment: '',  // Renamed from machinery_used
    process_description: '',
    sector: '',
    sub_sector: '',  // New field for sub-sector
    responsible_person: '',
    responsible_person_designation: '',
    responsible_person_contact: '',
    monitoring_frequency: 'monthly',
    reporting_frequency: 'monthly',
    attachments: [],
    other_information: '',  // Renamed from remarks
    equity_share_percentage: 100  // Default to 100%
  });

  // Helper function to download files
  const downloadFile = (url, filename) => {
    window.location.href = url;
  };

  // Validation function for auto-save - checks all mandatory fields
  // Suppliers only need name, regular users need full validation
  const validateFacilityForm = useCallback((data) => {
    // For suppliers, only name is required
    if (user?.user_type === 'supplier') {
      return data.name && data.name.trim() !== '';
    }
    
    // Check all mandatory fields for non-suppliers
    if (!data.name || data.name.trim() === '') return false;
    if (!data.sector || data.sector.trim() === '') return false;
    if (!data.address || data.address.trim() === '') return false;
    if (!data.city || data.city.trim() === '') return false;
    if (!data.state || data.state.trim() === '') return false;
    if (!data.country || data.country.trim() === '') return false;
    if (!data.pincode || data.pincode.trim() === '') return false;
    if (data.pincode && !/^\d{6}$/.test(data.pincode)) return false;
    if (!data.responsible_person || data.responsible_person.trim() === '') return false;
    
    // Check frequency validation
    const frequencyOrder = { 'daily': 1, 'weekly': 2, 'monthly': 3, 'quarterly': 4, 'yearly': 5 };
    const monitoringLevel = frequencyOrder[data.monitoring_frequency] || 3;
    const reportingLevel = frequencyOrder[data.reporting_frequency] || 3;
    if (monitoringLevel > reportingLevel) return false;
    
    return true;
  }, [user?.user_type]);

  // Auto-save handler
  const handleAutoSave = useCallback(async (data, isUpdate, existingId) => {
    const recordId = isUpdate ? existingId : (editingFacility?.id || autoSavedId);
    const safeData = {
      ...data,
      attachments: editingFacility?.attachments || [],
    };
    
    // Check for duplicate names
    const duplicate = facilities.find(f => 
      f.name.toLowerCase() === data.name.toLowerCase() && 
      (!recordId || f.id !== recordId)
    );
    
    if (duplicate) {
      throw new Error('A facility with this name already exists');
    }
    
    if (recordId) {
      // Update existing
      await axios.put(`${API}/facilities/${recordId}`, safeData, {
        headers: getAuthHeader()
      });
      fetchFacilities(); // Refresh list silently
      return { id: recordId };
    } else {
      // Create new
      const response = await axios.post(`${API}/facilities`, safeData, {
        headers: getAuthHeader()
      });
      const newId = response.data?.id;
      setAutoSavedId(newId);
      fetchFacilities(); // Refresh list silently
      return { id: newId };
    }
  }, [editingFacility, autoSavedId, facilities, getAuthHeader]);

  // Auto-save hook
  const { 
    saveStatus, 
    lastSavedAt, 
    errorMessage,
    triggerSave: triggerAutoSave,
    resetAutoSave 
  } = useAutoSave({
    onSave: handleAutoSave,
    validate: validateFacilityForm,
    formData,
    enabled: dialogOpen && !subscriptionExpired,
    inactivityMs: 5 * 60 * 1000, // 5 minutes
    isEditing: !!editingFacility,
    existingId: editingFacility?.id || autoSavedId
  });
  
  const validatePincode = (value) => {
    if (value && (!/^\d{6}$/.test(value))) {
      setPincodeError('Pincode must be exactly 6 digits');
      return false;
    }
    setPincodeError('');
    return true;
  };
  
  const handlePincodeChange = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setFormData({ ...formData, pincode: cleaned });
    if (cleaned) validatePincode(cleaned);
    else setPincodeError('');
  };

  const [newAttachment, setNewAttachment] = useState({ name: '', url: '' });

  useEffect(() => {
    fetchFacilities();
    fetchSectors();
    fetchOrganization();
  }, []);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get(`${API}/facilities`, {
        headers: getAuthHeader()
      });
      setFacilities(response.data);
    } catch (error) {
      console.error('Facilities fetch error:', error);
      setFacilities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    try {
      const response = await axios.get(`${API}/sectors`, {
        headers: getAuthHeader()
      });
      setSectors(response.data);
    } catch (error) {
      console.error('Sectors fetch error:', error);
      setSectors([]);
    }
  };

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${API}/organizations/my`, {
        headers: getAuthHeader()
      });
      setOrganization(response.data);
    } catch (error) {
      console.error('Organization fetch error:', error);
      setOrganization(null);
    }
  };

  const handleSameAsOrg = (checked) => {
    setSameAsOrg(checked);
    if (checked && organization) {
      // Copy organization details to form
      setFormData(prev => ({
        ...prev,
        name: organization.name || prev.name,
        address: organization.corporate_address || prev.address,
        city: organization.city || prev.city,
        state: organization.state || prev.state,
        country: organization.country || prev.country,
        pincode: organization.pincode || prev.pincode,
        process_description: organization.process_description || prev.process_description,
        responsible_person: organization.person_responsible || prev.responsible_person,
        reporting_frequency: organization.reporting_frequency || prev.reporting_frequency
      }));
    } else if (!checked) {
      // Clear the copied fields when unchecked
      setFormData(prev => ({
        ...prev,
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        process_description: '',
        responsible_person: '',
        reporting_frequency: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Suppliers only need name
    if (!isSupplier) {
      // Validation: Monitoring frequency must be shorter than or equal to reporting frequency
      const frequencyOrder = { 'daily': 1, 'weekly': 2, 'monthly': 3, 'quarterly': 4, 'yearly': 5 };
      const monitoringLevel = frequencyOrder[formData.monitoring_frequency] || 3;
      const reportingLevel = frequencyOrder[formData.reporting_frequency] || 3;
    
      if (monitoringLevel > reportingLevel) {
        toast.error('Monitoring frequency must be shorter than or equal to reporting frequency. (e.g., if reporting is monthly, monitoring can be daily, weekly, or monthly)');
        return;
      }
    }
    
    // Get the ID to check - either from editing or from auto-save
    const currentId = editingFacility?.id || autoSavedId;
    
    const duplicate = facilities.find(f => 
      f.name.toLowerCase() === formData.name.toLowerCase() && 
      (!currentId || f.id !== currentId)
    );
    
    if (duplicate) {
      toast.error('A facility with this name already exists');
      return;
    }
    
    try {
      const persistedAttachments = persistedEvidence(formData.attachments).map(toEvidencePayload);
      const baseData = { ...formData, attachments: persistedAttachments };
      let savedFacilityId = currentId;
      if (currentId) {
        // Update existing (either editing or auto-saved)
        await axios.put(`${API}/facilities/${currentId}`, baseData, {
          headers: getAuthHeader()
        });
      } else {
        // Create new
        const response = await axios.post(`${API}/facilities`, baseData, {
          headers: getAuthHeader()
        });
        savedFacilityId = response.data.id;
        setAutoSavedId(savedFacilityId);
        const hasMonthlyProduction = Object.values(productionDraft.monthlyData).some((quantity) => quantity !== '');
        if (productionDraft.quantity !== '' || hasMonthlyProduction) {
          const productionPayload = { input_type: productionDraft.inputType, unit: productionDraft.unit };
          if (productionDraft.inputType === 'monthly') {
            productionPayload.monthly_data = Object.fromEntries(Object.entries(productionDraft.monthlyData).filter(([, quantity]) => quantity !== '').map(([month, quantity]) => [month, { quantity: Math.max(0, parseFloat(quantity) || 0), unit: productionDraft.unit }]));
          } else {
            productionPayload.quantity = Math.max(0, parseFloat(productionDraft.quantity) || 0);
          }
          await axios.post(`${API}/facilities/${savedFacilityId}/production/${productionDraft.reportingYear}`, productionPayload, { headers: getAuthHeader() });
        }
      }

      const uploadResult = await uploadDraftEvidences({
        items: draftEvidence(formData.attachments),
        uploadUrl: `${API}/upload/evidence?bucket_type=org_facility&organization_id=${organization?.id || ''}`,
        headers: getAuthHeader(),
        mapUploaded: (uploaded, draft) => ({
          type: 'file',
          name: draft.name,
          url: uploaded.url,
          file_id: uploaded.file_id,
        }),
      });
      if (uploadResult.uploaded.length > 0) {
        try {
          await axios.put(`${API}/facilities/${savedFacilityId}`, {
            ...baseData,
            attachments: [...persistedAttachments, ...uploadResult.uploaded],
          }, { headers: getAuthHeader() });
        } catch (error) {
          await deleteEvidenceFiles(uploadResult.uploaded.map(getEvidenceFileId), API, getAuthHeader());
          throw error;
        }
      }

      const deletionFailures = await deleteEvidenceFiles(pendingDeletionIds.current, API, getAuthHeader());
      pendingDeletionIds.current.clear();
      revokeDraftEvidences(draftEvidence(formData.attachments));
      if (uploadResult.failed.length || deletionFailures) {
        toast.error(`Facility saved, but ${uploadResult.failed.length + deletionFailures} attachment change(s) could not be completed.`);
      }
      toast.success(currentId ? 'Facility updated successfully' : 'Facility created successfully');
      setDialogOpen(false);
      resetForm();
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleToggleActive = async () => {
    if (!facilityToToggle) return;
    
    try {
      const response = await axios.patch(
        `${API}/facilities/${facilityToToggle.id}/toggle-active`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle facility status');
    } finally {
      setToggleConfirmOpen(false);
      setFacilityToToggle(null);
    }
  };

  const handleDelete = async () => {
    if (!facilityToDelete) return;
    
    try {
      await axios.delete(`${API}/facilities/${facilityToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Facility and all related data deleted permanently');
      fetchFacilities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteConfirmOpen(false);
      setFacilityToDelete(null);
    }
  };

  const openEditDialog = (facility) => {
    setEditingFacility(facility);
    setFormData({
      name: facility.name,
      address: facility.address,
      city: facility.city || '',
      state: facility.state || '',
      country: facility.country || '',
      pincode: facility.pincode || '',
      products_services: facility.products_services || facility.products_manufactured || '',
      machinery_equipment: facility.machinery_equipment || facility.machinery_used || '',
      process_description: facility.process_description || '',
      sector: facility.sector || '',
      sub_sector: facility.sub_sector || '',
      responsible_person: facility.responsible_person || '',
      responsible_person_designation: facility.responsible_person_designation || '',
      responsible_person_contact: facility.responsible_person_contact || '',
      monitoring_frequency: facility.monitoring_frequency || 'monthly',
      reporting_frequency: facility.reporting_frequency || 'monthly',
      attachments: facility.attachments || [],
      other_information: facility.other_information || facility.remarks || '',
      equity_share_percentage: facility.equity_share_percentage ?? 100
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    revokeDraftEvidences(draftEvidence(formData.attachments));
    pendingDeletionIds.current.clear();
    setEditingFacility(null);
    setSameAsOrg(false);
    setAutoSavedId(null);
    setProductionDraft(createProductionDraft(organization?.reporting_year_type || 'financial_year'));
    resetAutoSave();
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
      products_services: '',
      machinery_equipment: '',
      process_description: '',
      sector: '',
      sub_sector: '',
      responsible_person: '',
      responsible_person_designation: '',
      responsible_person_contact: '',
      monitoring_frequency: 'monthly',
      reporting_frequency: 'monthly',
      attachments: [],
      other_information: '',
      equity_share_percentage: 100
    });
    setNewAttachment({ name: '', url: '' });
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const addAttachment = () => {
    if (!newAttachment.name || !newAttachment.url) {
      toast.error('Please provide both name and URL for attachment');
      return;
    }
    setFormData({
      ...formData,
      attachments: [...formData.attachments, { ...newAttachment }]
    });
    setNewAttachment({ type: 'link', name: '', url: '' });
  };

  const removeAttachment = async (index) => {
    const attachment = formData.attachments[index];
    const fileId = getEvidenceFileId(attachment);
    if (attachment?.is_draft) revokeDraftEvidence(attachment);
    if (!attachment?.is_draft && fileId) pendingDeletionIds.current.add(fileId);
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  // Filter facilities based on active status
  const filteredFacilities = showInactive 
    ? facilities 
    : facilities.filter(f => f.is_active !== false);

  const canEdit = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'user';
  const canDelete = user?.role === 'admin' || user?.role === 'super_admin'; // Only Admin can delete
  const canCreate = user?.role === 'admin'; // Only Admin can create new facilities
  
  // Check if user is a supplier - suppliers see simplified facility form
  const isSupplier = user?.user_type === 'supplier';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-7" data-testid="facilities-page">
      <ModulePageHeader
        title="Facilities"
        icon={Building2}
        iconClassName="border-amber-200 bg-amber-50 text-amber-700"
        testId="facilities"
        aside={<div className="flex items-center gap-4">
          {/* Show Inactive Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4 text-primary rounded"
            />
            <span className="text-sm text-text-muted">Show inactive</span>
          </label>
          
          {canCreate && (
            <Button 
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-6" 
              data-testid="add-facility-button"
              onClick={() => {
                if (subscriptionExpired) {
                  toast.error('Your subscription has expired. Please contact your administrator to renew.');
                  return;
                }
                resetForm(); 
                setDialogOpen(true); 
              }}
              disabled={subscriptionExpired}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Facility
            </Button>
          )}
        </div>}
      />
        
        {/* Dialog for both Create and Edit - shown when dialogOpen is true */}
        <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogContent className="max-h-[90vh] max-w-[1120px] gap-0 overflow-y-auto bg-white p-0 text-stone-900">
              <DialogHeader className="border-b border-stone-200 px-7 py-6">
                <div className="flex items-start justify-between gap-4"><div><DialogTitle className="text-xl font-semibold text-stone-900">{editingFacility ? 'Edit' : 'Add'} Facility</DialogTitle>{!isSupplier && <p className="mt-1 text-sm text-stone-500">Enter facility details below. You can update production quantity annually.</p>}</div>{!editingFacility && organization && !isSupplier && <label className="flex shrink-0 cursor-pointer items-center gap-2 pt-1 text-sm text-stone-600"><input type="checkbox" checked={sameAsOrg} onChange={(e) => handleSameAsOrg(e.target.checked)} className="h-4 w-4 rounded text-emerald-600" /><span>Use organisation details</span></label>}</div>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 bg-white px-7 py-6 [&_input]:bg-white [&_select]:bg-white [&_textarea]:bg-white">
                {/* Supplier form: facility name required; address and responsible-contact fields optional */}
                {isSupplier ? (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Facility Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="bg-stone-50"
                        placeholder="Enter your facility name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supplier-facility-sector">Sector/Industry</Label>
                      <select id="supplier-facility-sector" value={formData.sector} onChange={(e) => setFormData({ ...formData, sector: e.target.value })} className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 px-3" data-testid="supplier-add-facility-sector-select"><option value="">Select Sector</option>{sectors.map((sector) => <option key={sector.id} value={sector.name}>{sector.name}</option>)}</select>
                    </div>
                    <section className="space-y-4 rounded-lg border border-stone-200 p-4" data-testid="supplier-add-facility-address-details">
                      <div className="flex items-center gap-2 text-sm font-medium text-text-primary"><MapPin className="h-4 w-4" />Address Details</div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2 sm:col-span-2"><Label htmlFor="supplier-facility-address">Address details</Label><Input id="supplier-facility-address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-stone-50" data-testid="supplier-add-facility-address-input" /></div>
                        <div className="space-y-2"><Label htmlFor="supplier-facility-city">City</Label><Input id="supplier-facility-city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="bg-stone-50" data-testid="supplier-add-facility-city-input" /></div>
                        <div className="space-y-2"><Label htmlFor="supplier-facility-state">State / Province</Label><Input id="supplier-facility-state" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="bg-stone-50" data-testid="supplier-add-facility-state-input" /></div>
                        <div className="space-y-2"><Label htmlFor="supplier-facility-country">Country</Label><Input id="supplier-facility-country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="bg-stone-50" data-testid="supplier-add-facility-country-input" /></div>
                        <div className="space-y-2"><Label htmlFor="supplier-facility-pincode">PIN / ZIP Code</Label><Input id="supplier-facility-pincode" value={formData.pincode} onChange={(e) => handlePincodeChange(e.target.value)} maxLength={6} className={`bg-stone-50 ${pincodeError ? 'border-red-500' : ''}`} data-testid="supplier-add-facility-pincode-input" />{pincodeError && <p className="text-xs text-red-500" data-testid="supplier-add-facility-pincode-error">{pincodeError}</p>}</div>
                      </div>
                    </section>
                    <section className="space-y-4 border-t border-stone-100 pt-5" data-testid="supplier-add-facility-responsible-contact">
                      <h2 className="text-base font-semibold text-stone-900">Responsible Contact</h2>
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2"><Label htmlFor="supplier-responsible-person">Person Responsible</Label><Input id="supplier-responsible-person" value={formData.responsible_person} onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })} className="bg-stone-50" data-testid="supplier-add-facility-responsible-person-input" /></div>
                        <div className="space-y-2"><Label htmlFor="supplier-responsible-designation">Designation</Label><Input id="supplier-responsible-designation" value={formData.responsible_person_designation} onChange={(e) => setFormData({ ...formData, responsible_person_designation: e.target.value })} className="bg-stone-50" data-testid="supplier-add-facility-responsible-designation-input" /></div>
                        <div className="space-y-2"><Label htmlFor="supplier-responsible-contact">Contact Details</Label><Input id="supplier-responsible-contact" value={formData.responsible_person_contact} onChange={(e) => setFormData({ ...formData, responsible_person_contact: e.target.value })} className="bg-stone-50" placeholder="Email or phone" data-testid="supplier-add-facility-responsible-contact-input" /></div>
                      </div>
                    </section>
                  </div>
                ) : (
                  /* Full form for non-suppliers */
                  <>
                <div className="grid items-start gap-8 xl:grid-cols-[minmax(0,1fr)_21rem]">
                  <div className="min-w-0 space-y-6">
                <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)]">
                  <div className="min-w-0 space-y-2">
                    <Label htmlFor="name">Facility Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-stone-50"
                    />
                  </div>
                  <div className="min-w-0 space-y-2">
                    <Label htmlFor="sector" className="flex items-center gap-1.5">Sector/Industry *<TooltipProvider><Tooltip><TooltipTrigger asChild><span className="inline-flex cursor-help" data-testid="facility-sector-help"><Info className="h-3.5 w-3.5 text-stone-400" /></span></TooltipTrigger><TooltipContent>Contact Administrator to add new sectors</TooltipContent></Tooltip></TooltipProvider></Label>
                    <select
                      id="sector"
                      value={formData.sector}
                      onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                      required
                      className="w-full h-10 bg-stone-50 border border-stone-200 rounded-lg px-3"
                    >
                      <option value="">Select Sector</option>
                      {sectors.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0 space-y-2">
                    <Label htmlFor="sub_sector">Sub-Sector</Label>
                    <Input
                      id="sub_sector"
                      value={formData.sub_sector}
                      onChange={(e) => setFormData({ ...formData, sub_sector: e.target.value })}
                      placeholder="Enter sub-sector (optional)"
                      className="bg-stone-50"
                    />
                  </div>
                </div>

                {/* Address Section */}
                <div className="space-y-4 rounded-lg border border-stone-200 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                    <MapPin className="w-4 h-4" />
                    Address Details
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      className="bg-stone-50"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                        className="bg-stone-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State/Province *</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        required
                        className="bg-stone-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pincode">PIN/ZIP Code *</Label>
                      <Input
                        id="pincode"
                        value={formData.pincode}
                        onChange={(e) => handlePincodeChange(e.target.value)}
                        required
                        maxLength={6}
                        placeholder="6-digit pincode"
                        className={`bg-stone-50 ${pincodeError ? 'border-red-500' : ''}`}
                      />
                      {pincodeError && <p className="text-xs text-red-500">{pincodeError}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <select id="country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} required className="h-10 w-full border border-stone-200 bg-stone-50 px-3"><option value="">Select Country</option>{COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                </div>

                {/* Products/Services - Full width textarea */}
                <div className="space-y-2">
                  <Label htmlFor="products_services">Products/Services</Label>
                  <textarea
                    id="products_services"
                    value={formData.products_services}
                    onChange={(e) => setFormData({ ...formData, products_services: e.target.value })}
                    className="w-full min-h-[100px] px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg resize-y"
                    placeholder="Describe the products manufactured or services provided by this facility"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="machinery_equipment">Machinery and Equipments</Label>
                  <textarea
                    id="machinery_equipment"
                    value={formData.machinery_equipment}
                    onChange={(e) => setFormData({ ...formData, machinery_equipment: e.target.value })}
                    className="w-full min-h-[100px] px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg resize-y"
                    placeholder="Describe the machinery and equipment used in this facility"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="process_description">Process Description</Label>
                  <textarea
                    id="process_description"
                    value={formData.process_description}
                    onChange={(e) => setFormData({ ...formData, process_description: e.target.value })}
                    className="w-full min-h-[100px] px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg resize-y"
                    placeholder="Describe the manufacturing or operational processes at this facility"
                  />
                </div>

                <details className="border-t border-stone-200 pt-4">
                  <summary className="cursor-pointer text-sm font-medium text-stone-700">Additional facility details</summary>
                  <div className="mt-5 space-y-7">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="responsible_person">Person Responsible</Label>
                    <Input
                      id="responsible_person"
                      value={formData.responsible_person}
                      onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                      className="bg-stone-50"
                      data-testid="facility-person-responsible-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="responsible_person_designation">Designation</Label>
                    <Input
                      id="responsible_person_designation"
                      value={formData.responsible_person_designation}
                      onChange={(e) => setFormData({ ...formData, responsible_person_designation: e.target.value })}
                      className="bg-stone-50"
                      placeholder="e.g., Environmental Manager"
                      data-testid="facility-person-responsible-designation-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="responsible_person_contact">Contact Details</Label>
                    <Input
                      id="responsible_person_contact"
                      value={formData.responsible_person_contact}
                      onChange={(e) => setFormData({ ...formData, responsible_person_contact: e.target.value })}
                      className="bg-stone-50"
                      placeholder="Email or phone"
                      data-testid="facility-person-responsible-contact-input"
                    />
                  </div>
                </div>

                {/* Equity Share Percentage - Only show if organization uses equity share approach */}
                {organization?.org_boundaries_approach === 'equity_share' && (
                  <div className="mt-6 space-y-2 border-t border-stone-100 pt-6" data-testid="facility-equity-share-guidance">
                    <Label htmlFor="equity_share_percentage" className="flex items-center gap-1.5 font-medium text-text-primary">Equity Share Percentage (%) <span className="text-red-500">*</span><TooltipProvider><Tooltip><TooltipTrigger asChild><span className="inline-flex cursor-help" data-testid="facility-equity-share-help"><Info className="h-3.5 w-3.5 text-stone-400" /></span></TooltipTrigger><TooltipContent className="max-w-xs">Your organization uses the Equity Share Approach. Specify what percentage of this facility your organization owns.</TooltipContent></Tooltip></TooltipProvider></Label>
                    <Input
                      id="equity_share_percentage"
                      type="number"
                      min="0.01"
                      max="100"
                      step="0.01"
                      value={formData.equity_share_percentage}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (parseFloat(value) > 0 && parseFloat(value) <= 100)) {
                          setFormData({ ...formData, equity_share_percentage: value });
                        }
                      }}
                      className="w-32 bg-stone-50"
                      placeholder="e.g., 100"
                      data-testid="facility-equity-share-percentage-input"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="monitoring_frequency">Monitoring Frequency</Label>
                    <select
                      id="monitoring_frequency"
                      value={formData.monitoring_frequency}
                      onChange={(e) => setFormData({ ...formData, monitoring_frequency: e.target.value })}
                      className="w-full h-10 bg-stone-50 border border-stone-200 rounded-lg px-3"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reporting_frequency">Reporting Frequency</Label>
                    <select
                      id="reporting_frequency"
                      value={formData.reporting_frequency}
                      onChange={(e) => setFormData({ ...formData, reporting_frequency: e.target.value })}
                      className="w-full h-10 bg-stone-50 border border-stone-200 rounded-lg px-3"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>

                {/* Attachments Section */}
                <div className="space-y-6 rounded-lg border border-stone-200 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                    <Paperclip className="w-4 h-4" />
                    Attachments
                  </div>
                  
                  {formData.attachments.length > 0 && (
                    <div className="space-y-2">
                      {formData.attachments.map((att, idx) => {
                        // Determine if this is an uploaded file or external link
                        const isUploadedFile = att.is_draft || (att.url && (att.url.includes('/api/files/') || att.type === 'file'));
                        
                        // For uploaded files, construct proper view/download URLs
                        let viewUrl = att.is_draft ? att.preview_url : att.url;
                        let downloadUrl = att.url;
                        let uploadedFileId = null;
                        
                        if (isUploadedFile && !att.is_draft) {
                          // Extract file ID from URL patterns like:
                          // /api/files/{id}/view or /api/files/{id} or full URL with same pattern
                          const fileIdMatch = att.url.match(/\/api\/files\/([^\/]+)/);
                          if (fileIdMatch) {
                            uploadedFileId = fileIdMatch[1];
                            viewUrl = '#';
                          }
                        }
                        
                        return (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg">
                            {isUploadedFile ? (
                              <FileText className="w-4 h-4 text-blue-500" />
                            ) : (
                              <Link className="w-4 h-4 text-blue-500" />
                            )}
                            <span className="flex-1 text-sm truncate">{att.name}</span>
                            <a 
                              href={isUploadedFile ? viewUrl : ensureProtocol(att.url)} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              onClick={uploadedFileId ? (event) => { event.preventDefault(); void openSecureFile(uploadedFileId).catch(() => toast.error('Unable to open attachment')); } : undefined}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              title="View file"
                              data-testid={`facility-attachment-view-${idx}`}
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </a>
                            {/* Only show Download for uploaded files, not external links */}
                            {isUploadedFile && !att.is_draft && (
                              <button 
                                type="button"
                                onClick={async (e) => { 
                                  e.preventDefault(); 
                                  if (uploadedFileId) {
                                    await downloadSecureFile(uploadedFileId);
                                  } else {
                                    downloadFile(downloadUrl, att.name);
                                  }
                                }}
                                className="text-xs text-green-600 hover:underline flex items-center gap-1"
                                title="Download file"
                                data-testid={`facility-attachment-download-${idx}`}
                              >
                                <Download className="w-3 h-3" />
                                Download
                              </button>
                            )}
                            <Button type="button" size="sm" variant="ghost" onClick={() => removeAttachment(idx)} data-testid={`facility-attachment-remove-${idx}`}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add Link */}
                  <div className="space-y-2">
                    <Label className="text-sm">Add Link</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="Link Name"
                        value={newAttachment.name}
                        onChange={(e) => setNewAttachment({ ...newAttachment, name: e.target.value })}
                        className="bg-stone-50"
                      />
                      <Input
                        placeholder="URL"
                        value={newAttachment.url}
                        onChange={(e) => setNewAttachment({ ...newAttachment, url: e.target.value })}
                        className="bg-stone-50"
                      />
                      <Button type="button" variant="outline" onClick={addAttachment}>
                        <Plus className="w-4 h-4 mr-1" /> Add Link
                      </Button>
                    </div>
                  </div>

                  {/* Upload File */}
                  <div className="space-y-2">
                    <Label className="text-sm">Or Upload Files</Label>
                    <div 
                      className="border-2 border-dashed border-stone-300 rounded-lg p-4 text-center hover:border-primary transition-colors cursor-pointer"
                      onClick={() => document.getElementById('facility-file-upload')?.click()}
                    >
                      <input
                        id="facility-file-upload"
                        type="file"
                        className="hidden"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length === 0) return;
                          const newAttachments = [];
                          
                          for (const file of files) {
                            const sizeErr = validateFileSize(file);
                            if (sizeErr) {
                              toast.error(sizeErr);
                              continue;
                            }
                            newAttachments.push(createDraftEvidence(file, { type: 'file' }));
                          }
                          
                          if (newAttachments.length > 0) {
                            setFormData(prev => ({
                              ...prev,
                              attachments: [...prev.attachments, ...newAttachments]
                            }));
                            toast.success(`${newAttachments.length} file(s) uploaded`);
                          }
                          e.target.value = '';
                        }}
                        data-testid="facility-attachments-file-input"
                      />
                      <FileText className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                      <p className="text-sm text-text-muted">Drop files here or click to upload</p>
                      <p className="text-xs text-text-muted mt-1">PDF, Images, Excel, Word (Max 5MB) - Multiple files allowed</p>
                    </div>
                  </div>
                </div>

                {/* Other Information Section (renamed from Remarks/Notes) */}
                <div className="space-y-2">
                  <Label htmlFor="other_information">Other Information</Label>
                  <textarea
                    id="other_information"
                    value={formData.other_information || ''}
                    onChange={(e) => setFormData({ ...formData, other_information: e.target.value })}
                    rows={3}
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2"
                    placeholder="Add any additional information about this facility..."
                  />
                </div>

                  </div>
                </details>

                  </div>
                  <aside className="sticky top-0" data-testid="facility-production-panel">
                    {(editingFacility || autoSavedId) ? (
                      <FacilityProductionSection 
                        facilityId={editingFacility?.id || autoSavedId}
                        facilityName={formData.name}
                        readOnly={subscriptionExpired}
                        yearType={organization?.reporting_year_type || 'financial_year'}
                        defaultExpanded
                      />
                    ) : (
                      <div className="border border-stone-200 bg-white p-5"><div className="mb-5 flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50"><Package className="h-5 w-5 text-emerald-700" /></div><div><h3 className="text-sm font-semibold text-stone-900">Production Quantity</h3><p className="text-xs text-stone-500">Production for this facility</p></div></div><div className="space-y-4"><div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label htmlFor="facility-production-year">Financial Year</Label><Input id="facility-production-year" value={productionDraft.reportingYear} onChange={(event) => setProductionDraft({ ...productionDraft, reportingYear: event.target.value })} data-testid="facility-production-year" /></div><div className="space-y-2"><Label>Input Type</Label><div className="flex rounded-full bg-stone-100 p-1"><button type="button" onClick={() => setProductionDraft({ ...productionDraft, inputType: 'yearly' })} className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium ${productionDraft.inputType === 'yearly' ? 'bg-white text-emerald-700 shadow-sm' : 'text-stone-500'}`} data-testid="facility-production-yearly">Yearly</button><button type="button" onClick={() => setProductionDraft({ ...productionDraft, inputType: 'monthly' })} className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium ${productionDraft.inputType === 'monthly' ? 'bg-white text-emerald-700 shadow-sm' : 'text-stone-500'}`} data-testid="facility-production-monthly">Monthly</button></div></div></div><div className="space-y-2"><Label htmlFor="facility-production-unit">Unit</Label><Input id="facility-production-unit" value={productionDraft.unit} onChange={(event) => setProductionDraft({ ...productionDraft, unit: event.target.value })} placeholder="MT" data-testid="facility-production-unit" /></div>{productionDraft.inputType === 'yearly' ? <div className="space-y-2"><Label htmlFor="facility-production-quantity">Total Production</Label><Input id="facility-production-quantity" type="number" min="0" step="any" value={productionDraft.quantity} onChange={(event) => { if (event.target.value === '' || Number(event.target.value) >= 0) setProductionDraft({ ...productionDraft, quantity: event.target.value }); }} placeholder="Enter total production" data-testid="facility-production-quantity" /></div> : <div className="space-y-2"><Label>Monthly Production</Label><div className="grid grid-cols-3 gap-3">{PRODUCTION_MONTHS.map((month) => <div key={month} className="space-y-1"><Label className="text-xs text-stone-500">{month}</Label><Input type="number" min="0" value={productionDraft.monthlyData[month] || ''} onChange={(event) => { if (event.target.value === '' || Number(event.target.value) >= 0) setProductionDraft({ ...productionDraft, monthlyData: { ...productionDraft.monthlyData, [month]: event.target.value } }); }} placeholder="0" data-testid={`facility-production-month-${month}`} /></div>)}</div></div>}</div></div>
                    )}
                  </aside>
                </div>
                  </>
                )}

                <div className="flex justify-between items-center gap-3 pt-4 border-t border-stone-200">
                  <AutoSaveStatus 
                    status={saveStatus} 
                    lastSavedAt={lastSavedAt} 
                    errorMessage={errorMessage}
                  />
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => handleDialogChange(false)} data-testid="facility-form-cancel-button">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90 text-white" data-testid="facility-form-save-button">
                      {editingFacility || autoSavedId ? 'Update' : 'Create'} Facility
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredFacilities.map((facility) => (
          <Card key={facility.id} className="p-6 border border-stone-200 rounded-xl bg-white hover:shadow-lg transition-shadow" data-testid={`facility-card-${facility.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  {/* Deactivate/Activate button - Admin only */}
                  {canDelete && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => {
                        setFacilityToToggle(facility);
                        setToggleConfirmOpen(true);
                      }}
                      className={facility.is_active === false ? 'text-green-600' : 'text-amber-600'}
                      title={facility.is_active === false ? 'Activate Facility' : 'Deactivate Facility'}
                      data-testid={`toggle-facility-${facility.id}`}
                    >
                      {facility.is_active === false ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                    </Button>
                  )}
                  {/* Only show edit for active facilities */}
                  {facility.is_active !== false && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => {
                        if (subscriptionExpired) {
                          toast.error('Your subscription has expired. Please contact your administrator to renew.');
                          return;
                        }
                        openEditDialog(facility);
                      }} 
                      disabled={subscriptionExpired}
                      data-testid={`edit-facility-${facility.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  {/* Delete button - permanently deletes facility and all data */}
                  {canDelete && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => {
                        setFacilityToDelete(facility);
                        setDeleteConfirmOpen(true);
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Permanently delete facility"
                      data-testid={`delete-facility-${facility.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-heading font-bold text-text-primary">{facility.name}</h3>
              {facility.is_active === false && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">Inactive</span>
              )}
            </div>
            <div className="flex items-start gap-1 text-sm text-text-muted mb-2">
              {!isSupplier && <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <span>
                {facility.address}
                {facility.city && `, ${facility.city}`}
                {facility.state && `, ${facility.state}`}
                {facility.country && ` - ${facility.country}`}
                {facility.pincode && ` (${facility.pincode})`}
              </span>
            </div>
            {facility.sector && (
              <div className="inline-block px-3 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded-full mb-2">
                {facility.sector}
              </div>
            )}
            {/* Show Equity Share Percentage if org uses equity share approach */}
            {organization?.org_boundaries_approach === 'equity_share' && facility.equity_share_percentage != null && (
              <div className="inline-block ml-2 px-3 py-1 bg-stone-100 text-stone-700 text-xs font-medium rounded-full mb-2" data-testid={`facility-equity-share-${facility.id}`}>
                Equity: {facility.equity_share_percentage}%
              </div>
            )}
            {facility.attachments?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-stone-100">
                <p className="text-xs text-text-muted mb-1">{facility.attachments.length} attachment(s)</p>
              </div>
            )}
          </Card>
        ))}
      </div>

      {filteredFacilities.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto text-text-muted mb-4" />
          <h3 className="text-xl font-heading font-bold text-text-primary mb-2">No facilities yet</h3>
          <p className="text-text-secondary mb-4">Get started by adding your first facility</p>
        </div>
      )}

      {/* Toggle Active Confirmation Dialog */}
      <AlertDialog open={toggleConfirmOpen} onOpenChange={setToggleConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {facilityToToggle?.is_active === false ? 'Activate Facility' : 'Deactivate Facility'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {facilityToToggle?.is_active === false ? (
                <>Are you sure you want to <strong>activate</strong> &quot;{facilityToToggle?.name}&quot;? This will restore visibility of all emissions from this facility.</>
              ) : (
                <>Are you sure you want to <strong>deactivate</strong> &quot;{facilityToToggle?.name}&quot;? This will hide all emissions from this facility and prevent user assignments.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleToggleActive}
              className={facilityToToggle?.is_active === false ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {facilityToToggle?.is_active === false ? 'Activate' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              Permanently Delete Facility
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to <strong className="text-red-600">permanently delete</strong> &quot;{facilityToDelete?.name}&quot;?</p>
              <p className="text-red-600 font-medium">This action cannot be undone and will delete:</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>All emission records for this facility</li>
                <li>All sink records for this facility</li>
                <li>All attachments and files</li>
                <li>The facility itself</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
